<template>
  <div>
    <Head :title="run.name" />
    <trashed-message v-if="run.deleted_at && run.permissions.restore" class="mb-6" @restore="restore"> This run has been deleted. </trashed-message>

    <!-- Details -->
    <div class="lg:container lg:mx-auto">
      <div class="items-start justify-between md:flex">
        <!-- col-1 -->
        <div>
          <table class="w-full text-left">
            <tbody>
              <tr class="border-b">
                <th class="p-2">Run Name: </th>
                <td class="p-2">
                  <div v-if="! editName" class="flex">
                    {{ run.name }}
                    <a v-if="run.permissions.update" class="cursor-pointer" @click="editName = true">
                      <icon name="edit" class="flex-shrink-0 w-3 h-3 ml-2 fill-blue-400" />
                    </a>
                  </div>
                  <form v-else class="flex justify-around" @submit.prevent="update()">
                    <text-input v-model="form.name" :error="form.errors.name" class="w-full" label="" />
                    <loading-button :loading="form.processing" class="btn-blue" type="submit">Update</loading-button>
                  </form>
                </td>
              </tr>
              <tr class="border-b">
                <th class="p-2">System: </th>
                <td class="p-2">{{ run.title }}</td>
              </tr>
              <tr class="border-b">
                <th class="p-2">Grate: </th>
                <td class="p-2">{{ run.grate }}</td>
              </tr>
              <tr class="border-b">
                <th class="p-2">Overall Channel Depth</th>
                <td v-show="run.channel_depth" class="p-2">{{ run.channel_depth }}</td>
              </tr>
              <tr v-if="run.depth_restriction" class="border-b">
                <th class="p-2">Depth Restriction</th>
                <td class="p-2">{{ run.depth_restriction }}</td>
              </tr>
              <tr v-if="run.outlet_position" class="border-b">
                <th class="p-2">Outlet Position</th>
                <td class="p-2">{{ run.outlet_position }} m</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- col-2 -->
        <div class="flex flex-col items-end flex-auto mt-4 md:mt-0">
          <div class="flex items-center w-full mb-10 text-center md:flex-wrap md:justify-end">
            <Link class="px-4 py-3 mx-1 my-1 text-blue-800 bg-gray-200 border border-gray-400" :href="`/runs/${ run.id }`">Back to Run Details</Link>
            <Link class="px-4 py-3 mx-1 my-1 text-blue-800 bg-gray-200 border border-gray-400" :href="`/projects/${ run.project_id}`">Back to Project</Link>
            <!-- <button v-if="!run.deleted_at" class="px-4 py-3 mx-1 my-1 text-blue-800 bg-gray-200 border border-gray-400" type="button" @click="destroy">Delete Run</button> -->
            <a v-if="run.permissions.view" class="px-4 py-3 mx-1 my-1 text-blue-800 bg-gray-200 border border-gray-400" target="_blank" :href="`/runs/${ run.id }/drawing`">Download Drawing</a>
            <a class="px-4 py-3 mx-1 my-1 text-blue-800 bg-gray-200 border border-gray-400 cursor-pointer" @click="plainView = ! plainView">{{ plainView ? 'Plain / Top View' : 'Elevation / Side View' }}</a>
          </div>
          <!-- buttons -->
          <div class="mx-1">
            <span v-if="run.is_custom"><Link v-if="run.permissions.custom && run.permissions.update" class="btn-blue" :href="`/customs/${ run.id }/edit`">Edit Custom Run</Link></span>
            <span v-else><v-update-length v-if="run.permissions.update" :run="run" @processing="isMounted = $event" /></span>
          </div>
        </div>
      </div>
      <!-- flex -->
    </div>
    <!-- ! container -->

    <!-- Channels -->
    <v-drawings
      class="mx-4 my-20 aspect-video"
      :is-mounted="isMounted"
      :plain-view="plainView"
      :run="run"
      :part="part"
      :outlets="outlets"
      @input="setChannel($event)"
    />

    <div v-show="! isMounted" class="flex items-center justify-center my-20">
      <div aria-label="Loading..." role="status">
        <svg class="w-20 h-20 animate-spin" viewBox="3 3 18 18">
          <path
            class="fill-gray-200"
            d="M12 5C8.13401 5 5 8.13401 5 12C5 15.866 8.13401 19 12 19C15.866 19 19 15.866 19 12C19 8.13401 15.866 5 12 5ZM3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12Z"
          />
          <path
            class="fill-gray-800"
            d="M16.9497 7.05015C14.2161 4.31648 9.78392 4.31648 7.05025 7.05015C6.65973 7.44067 6.02656 7.44067 5.63604 7.05015C5.24551 6.65962 5.24551 6.02646 5.63604 5.63593C9.15076 2.12121 14.8492 2.12121 18.364 5.63593C18.7545 6.02646 18.7545 6.65962 18.364 7.05015C17.9734 7.44067 17.3403 7.44067 16.9497 7.05015Z"
          />
        </svg>
      </div>
    </div>

    <!-- Actions -->
    <v-actions v-if="isMounted" ref="actions" :run="run" :part="part" :actions="actions" @update="isMounted = $event" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import Icon from '@/Shared/Icon'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'
import TextInput from '@/Shared/TextInput'
import VUpdateLength from './Partials/Edit/UpdateLength'
import VDrawings from './Drawings/Index'
import VActions from './Partials/Edit/Actions'

export default {
  components: {
    Head,
    Link,
    Icon,
    LoadingButton,
    TrashedMessage,
    TextInput,
    VUpdateLength,
    VDrawings,
    VActions,
  },
  layout: Layout,
  props: {
    run     : Object,
    part    : Object,
    actions : Object,
    outlets : Object,
  },
  remember: 'form',
  data() {
    return {
      isMounted: false,
      plainView: false,
      editName : false,
      form : this.$inertia.form({
        name: this.run.name,
      }),
    }
  },
  computed:{
    parts() {
      return Object.values(this.run.sections).flat()
    },
    total() {
      return this.parts.map((ob) => parseFloat(ob.length)).reduce((partial, a) => partial + a , 0)
    },
    channels () {
      return this.parts.filter(el => el.type == 'channel')
    },
    accesories () {
      return this.parts.filter(el => el.type == 'accessory')
    },
  },
  mounted() {
    setTimeout(() => {
      this.isMounted = true
    }, 150)
  },
  methods: {
    update() {
      this.form.put(`/runs/${this.run.id}`, {
        preserveState: true,
        onBefore: () => {},
        onSuccess: () => { this.editName = false },
      })
    },
    getDetail (id) {
      this.$inertia.get( `/runs/${this.run.id}/edit/${id}`, {},
        {
          preserveState : true,
          preserveScroll: true,
          onBefore: () => {
            this.$refs.actions.form.action = null
            this.isMounted = false
          },
          onFinish: () => { this.isMounted = true },
        })
    },
    destroy() {
      if (confirm('Are you sure you want to delete this run?')) {
        this.$inertia
          .delete(`/runs/${this.run.id}`)
      }
    },
    restore() {
      if (confirm('Are you sure you want to restore this run?')) {
        this.$inertia.put(`/runs/${this.run.id}/restore`)
      }
    },
    setChannel (id) {
      this.getDetail(id)
    },
  },
}
</script>
